// AudioManager — high-level facade over Web Audio + the SFX presets.
//
//   - Owns the single AudioContext (created lazily after first user gesture
//     to satisfy browser autoplay policies).
//   - Mute toggle persisted to localStorage.
//   - Continuous sizzle effect driven by setSizzle(intensity) each frame.
//   - One-shot SFX via playSfx(name).
//
// The manager is cheap to construct. Nothing plays until resumeOnGesture()
// is called from a real user-gesture handler (pointerdown, keydown, etc.).

import { sfxDing, sfxBurn, sfxGameOver } from './sfx';

const STORAGE_KEY = 'marshmallow-madness:audio';

/** Named one-shot sound effects. */
export type SfxName = 'ding' | 'burn' | 'gameover';

const PLAYERS: Record<SfxName, (ctx: AudioContext, out: AudioNode) => void> = {
  ding: sfxDing,
  burn: sfxBurn,
  gameover: sfxGameOver,
};

interface AudioSettings {
  muted: boolean;
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Gain controlling continuous sizzle volume; null until AudioContext is live. */
  private sizzleGain: GainNode | null = null;
  private unlocked = false;
  private settings: AudioSettings;

  constructor(storage: Storage = globalThis.localStorage) {
    this.settings = loadSettings(storage);
    this._storage = storage;
  }

  // Storage kept for persist(); held in a private field rather than a
  // constructor parameter to avoid unused-parameter lint errors on the
  // readonly shorthand while still keeping the default-argument pattern.
  private readonly _storage: Storage;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Resume / create the AudioContext. **Must be called from a real
   * user-gesture handler** (pointerdown, keydown, touchstart …).
   * After this returns, playSfx() and setSizzle() are active.
   * Safe to call multiple times (idempotent after first unlock).
   */
  async resumeOnGesture(): Promise<void> {
    if (this.unlocked) {
      // Browsers may suspend the context on tab visibility changes.
      if (this.ctx && this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    try {
      const Ctx =
        globalThis.AudioContext ??
        (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.settings.muted ? 0 : 1;
      this.master.connect(this.ctx.destination);
      this.startSizzleLoop();
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      this.unlocked = true;
    } catch {
      // Audio unavailable; silently no-op forever.
      this.ctx = null;
      this.master = null;
    }
  }

  // ── SFX ───────────────────────────────────────────────────────────────────

  /**
   * Play a one-shot SFX. No-ops if audio is not yet unlocked or is muted.
   *
   * @param name - 'ding' (perfect toast), 'burn' (char), 'gameover'
   */
  playSfx(name: SfxName): void {
    if (!this.unlocked || !this.ctx || !this.master) return;
    if (this.settings.muted) return;
    PLAYERS[name](this.ctx, this.master);
  }

  // ── Sizzle ────────────────────────────────────────────────────────────────

  /**
   * Drive the continuous sizzle effect. Call each frame from PlayScene.
   *
   * @param intensity - 0 (silent) … 1 (full sizzle). Typically
   *   `(1 - distanceNorm)` scaled by how hot the marshmallow is.
   *   Smoothly ramped to avoid zipper noise. Respects mute state.
   */
  setSizzle(intensity: number): void {
    if (!this.sizzleGain || !this.ctx) return;
    const target = this.settings.muted ? 0 : clamp01(intensity) * 0.35;
    this.sizzleGain.gain.linearRampToValueAtTime(
      target,
      this.ctx.currentTime + 0.05,
    );
  }

  // ── Mute ──────────────────────────────────────────────────────────────────

  /** Mute all audio. Persisted to localStorage. */
  mute(): void {
    this.settings.muted = true;
    this.persist();
    this.applyMute();
  }

  /** Unmute all audio. Persisted to localStorage. */
  unmute(): void {
    this.settings.muted = false;
    this.persist();
    this.applyMute();
  }

  /**
   * Toggle mute state.
   * @returns New muted state (true = now muted).
   */
  toggleMute(): boolean {
    this.settings.muted = !this.settings.muted;
    this.persist();
    this.applyMute();
    return this.settings.muted;
  }

  /** Whether audio is currently muted. */
  isMuted(): boolean {
    return this.settings.muted;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  /**
   * Build a looping filtered-noise source for the sizzle effect.
   * The source runs forever at gain=0; setSizzle() raises/lowers it.
   */
  private startSizzleLoop(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const master = this.master;

    // 0.5 s of white noise — long enough that the loop seam is inaudible.
    const sr = ctx.sampleRate;
    const frames = Math.max(1, Math.floor(sr * 0.5));
    const buf = ctx.createBuffer(1, frames, sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

    // Band that sounds like frying: highpass ~1500 Hz, lowpass ~7000 Hz.
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 1500;

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 7000;

    const sizzleGain = ctx.createGain();
    sizzleGain.gain.value = 0;
    this.sizzleGain = sizzleGain;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(hp);
    hp.connect(lp);
    lp.connect(sizzleGain);
    sizzleGain.connect(master);
    src.start();
  }

  private applyMute(): void {
    if (!this.master) return;
    this.master.gain.value = this.settings.muted ? 0 : 1;
    // When muting, cancel pending sizzle ramps and silence immediately.
    if (this.settings.muted && this.sizzleGain && this.ctx) {
      this.sizzleGain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.sizzleGain.gain.setValueAtTime(0, this.ctx.currentTime);
    }
  }

  private persist(): void {
    try {
      this._storage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // localStorage may be unavailable in private mode; silently ignore.
    }
  }
}

function loadSettings(storage: Storage): AudioSettings {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { muted: false };
    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : false,
    };
  } catch {
    return { muted: false };
  }
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
